const zaokruhliNa2 = (cislo) => Math.round((Number(cislo) || 0) * 100) / 100;

const zaokruhliNa2AleboNull = (cislo) => {
  const hodnota = Number(cislo);

  return Number.isFinite(hodnota)
    ? Math.round(hodnota * 100) / 100
    : null;
};

const orez = (hodnota, min, max) => Math.min(max, Math.max(min, Number(hodnota) || 0));

function vypocitajDanZVynosu({
  danovyRezim,
  sadzbaDane,
  celkovyVynosPredDanou,
}) {
  const vynos = Math.max(0, Number(celkovyVynosPredDanou) || 0);
  const sadzba = orez(sadzbaDane, 0, 40);

  if (vynos <= 0 || danovyRezim !== "zdanit" || sadzba <= 0) {
    return 0;
  }

  return vynos * (sadzba / 100);
}

function vypocitajExternyJednorazovyNaklad(poplatky) {
  return poplatky?.typPoplatku === "jednorazovy"
    ? orez(poplatky?.jednorazovyPoplatok, 0, 9999999)
    : 0;
}

function vypocitajRealnyKlientskyZaklad({
  jednorazovyVklad,
  priebeh,
  externyJednorazovyNaklad,
  inflacia,
}) {
  const vkladNaStart = Math.max(0, Number(jednorazovyVklad) || 0);
  const externyNakladNaStart = Math.max(0, Number(externyJednorazovyNaklad) || 0);
  const priebehData = Array.isArray(priebeh) ? priebeh : [];
  const mesacnaInflacia = Math.pow(1 + (orez(inflacia, 0, 99) / 100), 1 / 12) - 1;

  const realneMesacneVklady = priebehData.reduce((sucet, bod) => {
    const mesiac = Math.max(0, Number(bod?.mesiac) || 0);
    const vklad = Math.max(0, Number(bod?.hrubyVklad) || 0);

    if (vklad <= 0) return sucet;

    return sucet + (
      mesacnaInflacia > 0 && mesiac > 0
        ? vklad / Math.pow(1 + mesacnaInflacia, mesiac)
        : vklad
    );
  }, 0);

  return vkladNaStart + externyNakladNaStart + realneMesacneVklady;
}

function vypocitajRocnyKlientskyVynos({
  jednorazovyVklad,
  priebeh,
  konecnaHodnotaPoDani,
}) {
  const priebehData = Array.isArray(priebeh) ? priebeh : [];
  const vkladNaStart = Math.max(0, Number(jednorazovyVklad) || 0);
  const konecnaHodnota = Math.max(0, Number(konecnaHodnotaPoDani) || 0);

  const mesacneVklady = priebehData.map((bod) => Math.max(0, Number(bod?.hrubyVklad) || 0));
  const celkoveVklady = vkladNaStart + mesacneVklady.reduce((sucet, vklad) => sucet + vklad, 0);

  if (celkoveVklady <= 0 || konecnaHodnota <= 0 || !priebehData.length) {
    return null;
  }

  const cashflowy = [-vkladNaStart];

  mesacneVklady.forEach((vklad) => {
    cashflowy.push(-vklad);
  });

  cashflowy[cashflowy.length - 1] += konecnaHodnota;

  const maVklad = cashflowy.some((cashflow) => cashflow < 0);
  const maVystup = cashflowy.some((cashflow) => cashflow > 0);

  if (!maVklad || !maVystup) {
    return null;
  }

  const npv = (mesacnaSadzba) => cashflowy.reduce(
    (sucet, cashflow, index) => sucet + (cashflow / Math.pow(1 + mesacnaSadzba, index)),
    0
  );

  let min = -0.9999;
  let max = 1;

  const npvMin = npv(min);
  const npvMax = npv(max);

  if (!Number.isFinite(npvMin) || !Number.isFinite(npvMax) || npvMin * npvMax > 0) {
    return null;
  }

  for (let i = 0; i < 120; i += 1) {
    const stred = (min + max) / 2;
    const hodnota = npv(stred);

    if (hodnota > 0) {
      min = stred;
    } else {
      max = stred;
    }
  }

  const mesacnyVynos = (min + max) / 2;
  const rocnyVynos = (Math.pow(1 + mesacnyVynos, 12) - 1) * 100;

  return Number.isFinite(rocnyVynos) && rocnyVynos > -99.99
    ? rocnyVynos
    : null;
}

function vypocitajMesacnyVkladPreMesiac({
  zakladnyMesacnyVklad,
  mesiac,
  rezimNavysovaniaVkladu,
  inflacia,
  fixneNavysenieVkladu,
}) {
  const rokIndex = Math.floor((Math.max(1, Number(mesiac) || 1) - 1) / 12);
  const zaklad = Math.max(0, Number(zakladnyMesacnyVklad) || 0);

  if (rezimNavysovaniaVkladu === "inflacia") {
    return zaklad * Math.pow(1 + (orez(inflacia, 0, 99) / 100), rokIndex);
  }

  if (rezimNavysovaniaVkladu === "fix") {
    return zaklad + (orez(fixneNavysenieVkladu, 0, 99999) * rokIndex);
  }

  return zaklad;
}

function vypocitajPriebeh({
  jednorazovyVklad,
  jednorazovyPrivkladZapnuty = false,
  jednorazovyPrivklad = 0,
  jednorazovyPrivkladMesiac = 0,
  mesacnyVklad,
  zhodnotenie,
  horizont,
  dodatocneZhodnocovanieZapnute = false,
  dodatocneZhodnocovanieRoky = 0,
  poplatky,
  dynamika = {},
}) {
  const vkladJednorazovo = orez(jednorazovyVklad, 0, 9999999);
  const vkladMesacne = orez(mesacnyVklad, 0, 99999);
  const rocneZhodnotenie = orez(zhodnotenie, 0, 99) / 100;
  const horizontRoky = orez(horizont, 0, 50);
  const dodatocneRoky = dodatocneZhodnocovanieZapnute
    ? orez(dodatocneZhodnocovanieRoky, 0, 50)
    : 0;
  const celkovaDobaInvesticieRoky = horizontRoky + dodatocneRoky;
  const pocetMesiacov = horizontRoky * 12;
  const pocetDodatocnychMesiacov = dodatocneRoky * 12;
  const celkovyPocetMesiacov = pocetMesiacov + pocetDodatocnychMesiacov;

  const najvyssiMesiacPrivkladu = Math.max(0, pocetMesiacov - 1);
  const jednorazovyPrivkladAktivny = Boolean(jednorazovyPrivkladZapnuty);

  const jednorazovyPrivkladHodnota = jednorazovyPrivkladAktivny && najvyssiMesiacPrivkladu > 0
    ? orez(jednorazovyPrivklad, 0, 9999999)
    : 0;

  const jednorazovyPrivkladMesiacHodnota = jednorazovyPrivkladHodnota > 0
    ? Math.min(
        najvyssiMesiacPrivkladu,
        Math.max(1, Math.floor(Number(jednorazovyPrivkladMesiac) || 1))
      )
    : 0;

  const jednorazovyPrivkladNaStart = 0;

  const mesacneZhodnotenie = Math.pow(1 + rocneZhodnotenie, 1 / 12) - 1;
  const manazerskyMesacne = (orez(poplatky?.manazerskyPoplatok, 0, 2.5) / 100) / 12;

  const jednorazovyPoplatok = poplatky?.typPoplatku === "jednorazovy"
    ? orez(poplatky?.jednorazovyPoplatok, 0, 9999999)
    : 0;

  const rozlozenyPoplatokCelkom = poplatky?.typPoplatku === "rozlozeny"
    ? orez(poplatky?.cielovaSuma, 0, 99999999) * (orez(poplatky?.rozlozenyPoplatokPercento, 0, 5) / 100)
    : 0;

  const maxRozlozenyPoplatokZVkladuPercento = orez(poplatky?.maxRozlozenyPoplatokZVkladuPercento, 0, 50) / 100;

  const planovanaRozlozenaSplatka = poplatky?.typPoplatku === "rozlozeny"
    ? vkladMesacne * maxRozlozenyPoplatokZVkladuPercento
    : 0;

  let zostavajuciRozlozenyPoplatok = rozlozenyPoplatokCelkom;

  const priebeznyPoplatokPercento = poplatky?.typPoplatku === "priebežny"
    ? orez(poplatky?.priebeznyPoplatokPercento, 0, 5) / 100
    : 0;
  let stav = vkladJednorazovo + jednorazovyPrivkladNaStart;
  let celkovePoplatky = jednorazovyPoplatok;
  const priebeh = [];
  let buducaHodnotaPoplatkov = 0;
  if (jednorazovyPoplatok > 0) {
    buducaHodnotaPoplatkov += jednorazovyPoplatok * Math.pow(1 + mesacneZhodnotenie, celkovyPocetMesiacov);
  }
  let celkoveVklady = vkladJednorazovo + jednorazovyPrivkladNaStart;
  let celkoveNavysenieVkladov = 0;

  for (let mesiac = 1; mesiac <= pocetMesiacov; mesiac += 1) {
    const aktualnyMesacnyVklad = vypocitajMesacnyVkladPreMesiac({
      zakladnyMesacnyVklad: vkladMesacne,
      mesiac,
      rezimNavysovaniaVkladu: dynamika.rezimNavysovaniaVkladu,
      inflacia: dynamika.inflacia,
      fixneNavysenieVkladu: dynamika.fixneNavysenieVkladu,
    });

    const jednorazovyPrivkladVMesiaci = jednorazovyPrivkladMesiacHodnota === mesiac
      ? jednorazovyPrivkladHodnota
      : 0;

    celkoveVklady += aktualnyMesacnyVklad + jednorazovyPrivkladVMesiaci;
    celkoveNavysenieVkladov += Math.max(0, aktualnyMesacnyVklad - vkladMesacne);

    let mesacnyVkladNaInvestovanie = aktualnyMesacnyVklad;
    let poplatokVMesiaci = 0;

    if (poplatky?.typPoplatku === "rozlozeny" && zostavajuciRozlozenyPoplatok > 0 && planovanaRozlozenaSplatka > 0) {
       const maxPoplatokZMesacnehoVkladu = aktualnyMesacnyVklad * maxRozlozenyPoplatokZVkladuPercento;

      const realnyRozlozenyPoplatok = Math.min(
        zostavajuciRozlozenyPoplatok,
        planovanaRozlozenaSplatka,
        maxPoplatokZMesacnehoVkladu,
        mesacnyVkladNaInvestovanie
      );

      mesacnyVkladNaInvestovanie -= realnyRozlozenyPoplatok;
      poplatokVMesiaci += realnyRozlozenyPoplatok;
      zostavajuciRozlozenyPoplatok -= realnyRozlozenyPoplatok;
    }

    if (priebeznyPoplatokPercento > 0) {
      const realnyPriebeznyPoplatok = aktualnyMesacnyVklad * priebeznyPoplatokPercento;
      mesacnyVkladNaInvestovanie -= realnyPriebeznyPoplatok;
      poplatokVMesiaci += realnyPriebeznyPoplatok;
    }

    mesacnyVkladNaInvestovanie = Math.max(0, mesacnyVkladNaInvestovanie);

    stav += mesacnyVkladNaInvestovanie + jednorazovyPrivkladVMesiaci;

    const stavPredZhodnotenim = stav;
    const stavPredManazerskym = stav * (1 + mesacneZhodnotenie);
    const priemernaHodnotaUctu = (stavPredZhodnotenim + stavPredManazerskym) / 2;
    const manazerskyPoplatokVMesiaci = Math.min(
      stavPredManazerskym,
      Math.max(0, priemernaHodnotaUctu * manazerskyMesacne)
    );

    stav = Math.max(0, stavPredManazerskym - manazerskyPoplatokVMesiaci);
    poplatokVMesiaci += manazerskyPoplatokVMesiaci;
    celkovePoplatky += poplatokVMesiaci;

    if (poplatokVMesiaci > 0) {
      buducaHodnotaPoplatkov += poplatokVMesiaci * Math.pow(1 + mesacneZhodnotenie, celkovyPocetMesiacov - mesiac);
    }

    priebeh.push({
      mesiac,
      rok: Math.ceil(mesiac / 12),
      hodnota: stav,
      poplatky: celkovePoplatky,
      cistyVklad: mesacnyVkladNaInvestovanie + jednorazovyPrivkladVMesiaci,
      hrubyVklad: aktualnyMesacnyVklad + jednorazovyPrivkladVMesiaci,
      pravidelnyVklad: aktualnyMesacnyVklad,
      jednorazovyPrivklad: jednorazovyPrivkladVMesiaci,
      zhodnotenieVMesiaci:
        stavPredManazerskym - stavPredZhodnotenim,
      jeDodatocneZhodnocovanie: false,
      poplatokVMesiaci,
    });
  }

  for (let dodatocnyMesiac = 1; dodatocnyMesiac <= pocetDodatocnychMesiacov; dodatocnyMesiac += 1) {
    const mesiac = pocetMesiacov + dodatocnyMesiac;

    let poplatokVMesiaci = 0;

    const stavPredZhodnotenim = stav;
    const stavPredManazerskym = stav * (1 + mesacneZhodnotenie);
    const priemernaHodnotaUctu = (stavPredZhodnotenim + stavPredManazerskym) / 2;
    const manazerskyPoplatokVMesiaci = Math.min(
      stavPredManazerskym,
      Math.max(0, priemernaHodnotaUctu * manazerskyMesacne)
    );

    stav = Math.max(0, stavPredManazerskym - manazerskyPoplatokVMesiaci);
    poplatokVMesiaci += manazerskyPoplatokVMesiaci;
    celkovePoplatky += poplatokVMesiaci;

    if (poplatokVMesiaci > 0) {
      buducaHodnotaPoplatkov += poplatokVMesiaci * Math.pow(1 + mesacneZhodnotenie, celkovyPocetMesiacov - mesiac);
    }

    priebeh.push({
      mesiac,
      rok: Math.ceil(mesiac / 12),
      hodnota: stav,
      poplatky: celkovePoplatky,
      cistyVklad: 0,
      hrubyVklad: 0,
      pravidelnyVklad: 0,
      jednorazovyPrivklad: 0,
      zhodnotenieVMesiaci:
        stavPredManazerskym - stavPredZhodnotenim,
      jeDodatocneZhodnocovanie: true,
      poplatokVMesiaci,
    });
  }

  const konecnaHodnota = stav;
  const celkovyVynos = konecnaHodnota - celkoveVklady;

  const jednoduchyVynosPercento = celkoveVklady > 0
    ? (celkovyVynos / celkoveVklady) * 100
    : 0;

  let twrKumulativne = 1;

  for (let mesiac = 1; mesiac <= celkovyPocetMesiacov; mesiac += 1) {
    const bod = priebeh[mesiac - 1];

    const predoslyStav = mesiac === 1
      ? Math.max(0, vkladJednorazovo + jednorazovyPrivkladNaStart)
      : priebeh[mesiac - 2]?.hodnota || 0;

    const aktualnyStav = Number(bod?.hodnota) || 0;
    const hrubyVkladVMesiaci = Number(bod?.hrubyVklad) || 0;

    const zakladPreVynos = predoslyStav + hrubyVkladVMesiaci;

    if (zakladPreVynos > 0) {
      const mesacnyVynos = (aktualnyStav - zakladPreVynos) / zakladPreVynos;

      if (Number.isFinite(mesacnyVynos) && mesacnyVynos > -1) {
        twrKumulativne *= (1 + mesacnyVynos);
      }
    }
  }

  const realnaHodnotaPoInflacii = celkovaDobaInvesticieRoky > 0
    ? konecnaHodnota / Math.pow(1 + (orez(dynamika.inflacia, 0, 99) / 100), celkovaDobaInvesticieRoky)
    : konecnaHodnota;

  const roiPercento = celkoveVklady > 0
    ? ((konecnaHodnota - celkoveVklady) / celkoveVklady) * 100
    : 0;

  const kumulovanyVynosPercento = (twrKumulativne - 1) * 100;

  const casovoVazenyVynosPercento = celkovaDobaInvesticieRoky > 0
    ? ((Math.pow(twrKumulativne, 1 / celkovaDobaInvesticieRoky) - 1) * 100)
    : 0;

  return {
    konecnaHodnota,
    celkoveVklady,
    celkoveNavysenieVkladov,
    celkovyVynos,
    celkovePoplatky,
    roiPercento,
    realnaHodnotaPoInflacii,
    realnyCistyVynosPoInflacii: realnaHodnotaPoInflacii - celkoveVklady,
    buducaHodnotaPoplatkov,
    jednoduchyVynosPercento,
    kumulovanyVynosPercento,
    casovoVazenyVynosPercento,
    pocetMesiacov: celkovyPocetMesiacov,
    pocetMesiacovVkladania: pocetMesiacov,
    pocetDodatocnychMesiacov,
    celkovaDobaInvesticieRoky,
    priebeh,
  };
}

export function vypocitajInvesticnuKalkulacku({
  jednorazovyVklad,
  jednorazovyPrivkladZapnuty = false,
  jednorazovyPrivklad = 0,
  jednorazovyPrivkladMesiac = 0,
  mesacnyVklad,
  zhodnotenie,
  horizont,
  dodatocneZhodnocovanieZapnute = false,
  dodatocneZhodnocovanieRoky = 0,
  cielovaSuma,
  typPoplatku,
  jednorazovyPoplatok,
  rozlozenyPoplatokPercento,
  rozlozenyPoplatokSplatky,
  maxRozlozenyPoplatokZVkladuPercento,
  priebeznyPoplatokPercento,
  manazerskyPoplatok,
  inflacia = 2,
  rezimNavysovaniaVkladu = "ziadne",
  fixneNavysenieVkladu = 0,
  danovyRezim = "bezDane",
  sadzbaDane = 19,
  oslobodenieVynosu = 500,
}) {
  const celkovaDobaInvesticieRoky = orez(horizont, 0, 50) + (
    dodatocneZhodnocovanieZapnute
      ? orez(dodatocneZhodnocovanieRoky, 0, 50)
      : 0
  );

  const poplatky = {
    cielovaSuma,
    typPoplatku,
    jednorazovyPoplatok,
    rozlozenyPoplatokPercento,
    rozlozenyPoplatokSplatky,
    maxRozlozenyPoplatokZVkladuPercento,
    priebeznyPoplatokPercento,
    manazerskyPoplatok,
  };

  const bezPoplatkov = vypocitajPriebeh({
    jednorazovyVklad,
    jednorazovyPrivkladZapnuty,
    jednorazovyPrivklad,
    jednorazovyPrivkladMesiac,
    mesacnyVklad,
    zhodnotenie,
    horizont,
    dodatocneZhodnocovanieZapnute,
    dodatocneZhodnocovanieRoky,
    poplatky: {
      ...poplatky,
      typPoplatku: "ziadny",
      manazerskyPoplatok: 0,
    },
    dynamika: {
      inflacia,
      rezimNavysovaniaVkladu,
      fixneNavysenieVkladu,
    },
  });

  const sPoplatkami = vypocitajPriebeh({
    jednorazovyVklad,
    jednorazovyPrivkladZapnuty,
    jednorazovyPrivklad,
    jednorazovyPrivkladMesiac,
    mesacnyVklad,
    zhodnotenie,
    horizont,
    poplatky,
    dodatocneZhodnocovanieZapnute,
    dodatocneZhodnocovanieRoky,
    dynamika: {
      inflacia,
      rezimNavysovaniaVkladu,
      fixneNavysenieVkladu,
    },
  });

  const bezNavysovania = vypocitajPriebeh({
    jednorazovyVklad,
    jednorazovyPrivkladZapnuty,
    jednorazovyPrivklad,
    jednorazovyPrivkladMesiac,
    mesacnyVklad,
    zhodnotenie,
    horizont,
    poplatky,
    dodatocneZhodnocovanieZapnute,
    dodatocneZhodnocovanieRoky,
    dynamika: {
      inflacia,
      rezimNavysovaniaVkladu: "ziadne",
      fixneNavysenieVkladu: 0,
    },
  });

  const dodatocneZhodnocovanieRokyHodnota = dodatocneZhodnocovanieZapnute
    ? orez(dodatocneZhodnocovanieRoky, 0, 50)
    : 0;

  const bezDodatocnehoZhodnotenia = dodatocneZhodnocovanieRokyHodnota > 0
    ? vypocitajPriebeh({
        jednorazovyVklad,
        jednorazovyPrivkladZapnuty,
        jednorazovyPrivklad,
        jednorazovyPrivkladMesiac,
        mesacnyVklad,
        zhodnotenie,
        horizont,
        poplatky,
        dodatocneZhodnocovanieZapnute: false,
        dodatocneZhodnocovanieRoky: 0,
        dynamika: {
          inflacia,
          rezimNavysovaniaVkladu,
          fixneNavysenieVkladu,
        },
      })
    : null;

  const externyJednorazovyNaklad = vypocitajExternyJednorazovyNaklad(poplatky);

  const vypocitajVystupPrePriebeh = (data) => {
    const konecnaHodnota = Math.max(0, Number(data?.konecnaHodnota) || 0);
    const celkoveVklady = Math.max(0, Number(data?.celkoveVklady) || 0);
    const celkovePoplatky = Math.max(0, Number(data?.celkovePoplatky) || 0);
    const klientskyZaklad = celkoveVklady + externyJednorazovyNaklad;

    const celkovyVynosPredDanou = Math.max(0, konecnaHodnota - klientskyZaklad);

    const danZVynosu = vypocitajDanZVynosu({
      danovyRezim,
      sadzbaDane,
      celkovyVynosPredDanou,
    });

    const konecnaHodnotaPoDani = Math.max(0, konecnaHodnota - danZVynosu);
    const celkovyVynosPoDani = Math.max(0, celkovyVynosPredDanou - danZVynosu);

    const roiPoDaniPercento = klientskyZaklad > 0
      ? (celkovyVynosPoDani / klientskyZaklad) * 100
      : null;

    const nasobokVkladov = klientskyZaklad > 0
      ? konecnaHodnota / klientskyZaklad
      : null;

    const realnaHodnotaPoInflacii = celkovaDobaInvesticieRoky > 0
      ? konecnaHodnotaPoDani / Math.pow(1 + (orez(inflacia, 0, 99) / 100), celkovaDobaInvesticieRoky)
      : konecnaHodnotaPoDani;

    const realnyKlientskyZaklad = vypocitajRealnyKlientskyZaklad({
      jednorazovyVklad,
      priebeh: data?.priebeh,
      externyJednorazovyNaklad,
      inflacia,
    });

    const realnyCistyVynosPoInflacii = realnaHodnotaPoInflacii - realnyKlientskyZaklad;

    const realneZhodnoteniePoInflaciiPercento = realnyKlientskyZaklad > 0
      ? (realnyCistyVynosPoInflacii / realnyKlientskyZaklad) * 100
      : null;

    return {
      konecnaHodnota,
      celkoveVklady,
      celkovePoplatky,
      klientskyZaklad,
      celkovyVynosPredDanou,
      danZVynosu,
      konecnaHodnotaPoDani,
      celkovyVynosPoDani,
      roiPoDaniPercento,
      nasobokVkladov,
      realnaHodnotaPoInflacii,
      realnyCistyVynosPoInflacii,
      realneZhodnoteniePoInflaciiPercento,
    };
  };

  const vystupSPoplatkami = vypocitajVystupPrePriebeh(sPoplatkami);
  const vystupBezNavysovania = vypocitajVystupPrePriebeh(bezNavysovania);
  const vystupBezDodatocnehoZhodnotenia = bezDodatocnehoZhodnotenia
    ? vypocitajVystupPrePriebeh(bezDodatocnehoZhodnotenia)
    : null;

  const zobrazenaKonecnaHodnota = vystupSPoplatkami.konecnaHodnota;
  const konecnaHodnotaPoDani = vystupSPoplatkami.konecnaHodnotaPoDani;
  const danZVynosu = vystupSPoplatkami.danZVynosu;
  const celkovyVynosPredDanou = vystupSPoplatkami.celkovyVynosPredDanou;
  const celkovyVynosPoDani = vystupSPoplatkami.celkovyVynosPoDani;
  const nakladyKlienta = vystupSPoplatkami.klientskyZaklad;

  const prinosNavyseniaVkladov = rezimNavysovaniaVkladu === "ziadne"
    ? 0
    : Math.max(0, vystupSPoplatkami.celkovyVynosPoDani - vystupBezNavysovania.celkovyVynosPoDani);

  const prinosDodatocnehoZhodnotenia = dodatocneZhodnocovanieRokyHodnota > 0 && vystupBezDodatocnehoZhodnotenia
    ? Math.max(0, konecnaHodnotaPoDani - vystupBezDodatocnehoZhodnotenia.konecnaHodnotaPoDani)
    : 0;

  const roiPoDaniPercento = vystupSPoplatkami.roiPoDaniPercento;
  const nasobokVkladov = vystupSPoplatkami.nasobokVkladov;

  const rocnyKlientskyVynosPercento = vypocitajRocnyKlientskyVynos({
    jednorazovyVklad,
    priebeh: sPoplatkami.priebeh,
    konecnaHodnotaPoDani,
  });

  const realnaHodnotaPoInflaciiPoDani = vystupSPoplatkami.realnaHodnotaPoInflacii;
  const realnyCistyVynosPoInflacii = vystupSPoplatkami.realnyCistyVynosPoInflacii;
  const realneZhodnoteniePoInflaciiPercento = vystupSPoplatkami.realneZhodnoteniePoInflaciiPercento;

  const strataNaHodnotePoplatkami = Math.max(
    0,
    Number(sPoplatkami.buducaHodnotaPoplatkov) || (bezPoplatkov.konecnaHodnota - sPoplatkami.konecnaHodnota)
  );

  const hrubyVynosBezPoplatkov = bezPoplatkov.konecnaHodnota - bezPoplatkov.celkoveVklady;

  const efektPoplatkovPercento = hrubyVynosBezPoplatkov > 0
    ? (strataNaHodnotePoplatkami / hrubyVynosBezPoplatkov) * 100
    : 0;

  const navysenieVkladov = rezimNavysovaniaVkladu === "ziadne"
    ? 0
    : Math.max(0, Number(sPoplatkami.celkoveNavysenieVkladov) || 0);

  const dopadNavysovaniaNaPoplatky = rezimNavysovaniaVkladu === "ziadne"
    ? 0
    : Math.max(0, (Number(sPoplatkami.celkovePoplatky) || 0) - (Number(bezNavysovania.celkovePoplatky) || 0));

  return {
    zhodnotenie: zaokruhliNa2(zhodnotenie),
    inflacia: zaokruhliNa2(inflacia),

    konecnaHodnota: zaokruhliNa2(zobrazenaKonecnaHodnota),
    konecnaHodnotaPredDanou: zaokruhliNa2(sPoplatkami.konecnaHodnota),
    konecnaHodnotaPoDani: zaokruhliNa2(konecnaHodnotaPoDani),

    celkoveVklady: zaokruhliNa2(sPoplatkami.celkoveVklady),
    nakladyKlienta: zaokruhliNa2(nakladyKlienta),
    navysenieVkladov: zaokruhliNa2(navysenieVkladov),
    prinosNavyseniaVkladov: zaokruhliNa2(prinosNavyseniaVkladov),
    dopadNavysovaniaNaPoplatky: zaokruhliNa2(dopadNavysovaniaNaPoplatky),
    rezimNavysovaniaVkladu,
    fixneNavysenieVkladu: zaokruhliNa2(fixneNavysenieVkladu),

    celkovyVynos: zaokruhliNa2(celkovyVynosPoDani),
    celkovyVynosPredDanou: zaokruhliNa2(celkovyVynosPredDanou),

    danZVynosu: zaokruhliNa2(danZVynosu),
    celkovePoplatky: zaokruhliNa2(sPoplatkami.celkovePoplatky),

    celkoveZhodnotenieVkladuPercento: zaokruhliNa2AleboNull(roiPoDaniPercento),
    roiPercento: zaokruhliNa2AleboNull(roiPoDaniPercento),
    nasobokVkladov: zaokruhliNa2AleboNull(nasobokVkladov),
    rocnyKlientskyVynosPercento: zaokruhliNa2AleboNull(rocnyKlientskyVynosPercento),

    realnaHodnotaPoInflacii: zaokruhliNa2(realnaHodnotaPoInflaciiPoDani),
    realnyCistyVynosPoInflacii: zaokruhliNa2(realnyCistyVynosPoInflacii),
    realneZhodnoteniePoInflaciiPercento: zaokruhliNa2AleboNull(realneZhodnoteniePoInflaciiPercento),

    prinosDodatocnehoZhodnotenia: zaokruhliNa2(prinosDodatocnehoZhodnotenia),
    dodatocneZhodnocovanieRoky: zaokruhliNa2(dodatocneZhodnocovanieRokyHodnota),

    hrubyVynosBezPoplatkov: zaokruhliNa2(hrubyVynosBezPoplatkov),
    efektPoplatkovPercento: zaokruhliNa2(efektPoplatkovPercento),

    jednoduchyVynosPercento: zaokruhliNa2AleboNull(roiPoDaniPercento),
    kumulovanyVynosPercento: zaokruhliNa2AleboNull(roiPoDaniPercento),

    pocetMesiacov: sPoplatkami.pocetMesiacov,
    pocetMesiacovVkladania: sPoplatkami.pocetMesiacovVkladania,
    pocetDodatocnychMesiacov: sPoplatkami.pocetDodatocnychMesiacov,
    celkovaDobaInvesticieRoky,

    strataNaHodnotePoplatkami: zaokruhliNa2(strataNaHodnotePoplatkami),
    priebeh: sPoplatkami.priebeh,
    priebehBezPoplatkov: bezPoplatkov.priebeh,
    priebehBezNavysovania: rezimNavysovaniaVkladu === "ziadne" ? [] : bezNavysovania.priebeh,
    rozdielNavysovania: rezimNavysovaniaVkladu === "ziadne"
      ? 0
      : zaokruhliNa2(sPoplatkami.konecnaHodnota - bezNavysovania.konecnaHodnota),
  };
}